import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { API_URL, ApiResponse } from '../config/api.config';
import { ApiSessionService } from './api-session.service';

/**
 * Favorite screens of the signed-in user (/favorites), shared by the Topbar's
 * star toggle and the Favorites page. Changes are applied locally right away.
 */
@Injectable({ providedIn: 'root' })
export class FavoritesService {
  private readonly http = inject(HttpClient);
  private readonly favorites$ = new BehaviorSubject<Set<string>>(new Set());
  private loaded = false;

  constructor() {
    inject(ApiSessionService).cleared$.subscribe(() => {
      this.loaded = false;
      this.favorites$.next(new Set());
    });
  }

  getAll(): Observable<string[]> {
    this.ensureLoaded();
    return this.favorites$.pipe(map((set) => Array.from(set)));
  }

  isFavorited(route: string): Observable<boolean> {
    this.ensureLoaded();
    return this.favorites$.pipe(map((set) => set.has(route)));
  }

  toggle(route: string): void {
    if (this.favorites$.value.has(route)) {
      this.remove(route);
    } else {
      this.favorites$.next(new Set([...this.favorites$.value, route]));
      this.sync(this.http.post<ApiResponse<string[]>>(`${API_URL}/favorites`, { route }));
    }
  }

  remove(route: string): void {
    const next = new Set(this.favorites$.value);
    next.delete(route);
    this.favorites$.next(next);
    this.sync(this.http.delete<ApiResponse<string[]>>(`${API_URL}/favorites`, { body: { route } }));
  }

  private ensureLoaded(): void {
    if (this.loaded) return;
    this.loaded = true;
    this.sync(this.http.get<ApiResponse<string[]>>(`${API_URL}/favorites`));
  }

  private sync(request$: Observable<ApiResponse<string[]>>): void {
    request$.subscribe({
      next: (response) => this.favorites$.next(new Set(response.data)),
      error: (error) => console.error('[API] favorites', error),
    });
  }
}
